import { type FC, type FormEvent, useId, useState } from 'react';
import { useNavigate } from 'react-router';

import Kbd, { modKeyLabel } from '@/components/reusable/kbd';
import { pageurl } from '@/constants/pageurl';

import { HC_COPY } from '../HelpCentre.Copy';

interface ISearchField {
	initial?: string;
	/** `hero` on the home page; `compact` on the search results page. */
	size?: 'hero' | 'compact';
	/** Controlled mode (search page): typing updates results live instead of navigating. */
	onChange?: (q: string) => void;
	autoFocus?: boolean;
	inputRef?: (el: HTMLInputElement | null) => void;
}

export const searchUrl = (q: string) => `${pageurl.HELP_SEARCH}?q=${encodeURIComponent(q.trim())}`;

const SearchField: FC<ISearchField> = ({ initial = '', size = 'hero', onChange, autoFocus, inputRef }) => {
	const [q, setQ] = useState(initial);
	const navigate = useNavigate();
	const id = useId();

	const onSubmit = (e: FormEvent) => {
		e.preventDefault();
		if (q.trim().length === 0) return;
		navigate(searchUrl(q));
	};

	return (
		<form
			role='search'
			className={`hc-search hc-search--${size}`}
			onSubmit={onSubmit}
		>
			<label
				htmlFor={id}
				className='visually-hidden'
			>
				{HC_COPY.searchLabel}
			</label>
			<svg
				className='hc-search__icon'
				width='18'
				height='18'
				viewBox='0 0 24 24'
				fill='none'
				stroke='currentColor'
				strokeWidth='2'
				strokeLinecap='round'
				aria-hidden='true'
				focusable='false'
			>
				<circle
					cx='11'
					cy='11'
					r='7'
				/>
				<path d='M20 20l-3.5-3.5' />
			</svg>
			<input
				id={id}
				ref={inputRef}
				type='search'
				className='hc-search__input'
				placeholder={HC_COPY.searchPlaceholder}
				value={q}
				autoFocus={autoFocus}
				autoComplete='off'
				onChange={(e) => {
					setQ(e.target.value);
					onChange?.(e.target.value);
				}}
			/>
			{size === 'hero' ? (
				<span
					className='hc-search__kbd'
					aria-hidden='true'
				>
					<Kbd>{modKeyLabel()}</Kbd>
					<Kbd>K</Kbd>
				</span>
			) : null}
			<button
				type='submit'
				className='hc-search__submit'
			>
				{HC_COPY.searchSubmit}
			</button>
		</form>
	);
};

export default SearchField;
